import {useCallback, useMemo, useRef, useState} from 'react';
import * as THREE from 'three';
import {
    OperationGroup,
    Dimension,
    PreviewLocation,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import useMaterialTexture from 'components/customer/Preview3D/lib/useMaterialTexture';
import {Animation} from 'components/customer/Preview3D/animation/entity/Animation';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {toggleDoorHandler} from 'components/customer/Preview3D/animation/doorAnimationHandler';
import {toggleCornerHandler} from 'components/customer/Preview3D/animation/cornerAnimationHandler';
import {toggleDrawerHandler} from 'components/customer/Preview3D/animation/drawerAnimationHandler';
import {toggleApplianceExtendedDoorHandler} from 'components/customer/Preview3D/animation/applianceAnimationHandler';
import {toggleMainBifoldDoorHandler} from 'components/customer/Preview3D/animation/mainBifoldDoorAnimationHandler';
import {toggleExtendedBifoldDoorHandler} from 'components/customer/Preview3D/animation/extendedBifoldDoorAnimationHandler';
import {useSurveyButton} from 'shared/components/Surveys/helpers/useSurveyButton';
import {SURVEYS} from 'shared/components/Surveys/helpers/useSurveys';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import {highLiftAnimationHandler} from './highLiftAnimationHandler';
import {highSwingAnimationHandler} from './highSwingAnimationHandler';
import {toggleCornerDrawerHandler} from 'components/customer/Preview3D/animation/cornerDrawerAnimationHandler';
import {getPartAttributes} from 'components/customer/RoomPlanner/helpers/getProductAttributes';
import {
    getFoldKitReturnPanelWidth,
    toggleFoldKitDoorHandler,
} from 'components/customer/Preview3D/animation/foldKitDoorAnimationHandler';
import {CabinetDrawerType} from 'components/customer/Product/entity/Cabinet';
import {getPantryAwareRightLShapeBifoldDoorX} from 'components/customer/Preview3DCommon/values/door';
import {createReturnDoorAnimationHandler} from 'components/customer/Preview3DCommon/animations/returnDoor';
import {
    isShakerFoldKitDoor,
    isSplitDoorOperationGroup,
} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';

// Fallback in case a stalled render loop never ticks TWEEN.update for the zero-duration snap-open tweens.
const SNAP_OPEN_TIMEOUT_MS = 3000;

enum AnimationType {
    DOOR,
    DRAWER,
}
interface AnimationEvents {
    animation: Animation;
    type: AnimationType;
}

const useDoorAndDrawerAnimation = (
    isDoorOpen: boolean,
    hidden: boolean,
    raw: boolean,
    previewLocation: PreviewLocation = 'SPECS'
) => {
    const isDoorOpenRef = useRef(isDoorOpen);

    const openAnimations = useRef<AnimationEvents[]>([]);
    const closeAnimations = useRef<AnimationEvents[]>([]);
    const pendingSnapOpens = useRef<Promise<unknown>[]>([]);

    const [prefix, setPrefix] = useState('Close');

    const {exteriorThickness, carcaseThickness} = useMaterialTexture();
    const {showSurveyForm} = useSurveyButton(SURVEYS.ProductVisualizer3d, true);

    const {values, productDataStore} = use3DContext();
    const variables = useMemo(
        () =>
            JSON.parse(
                productDataStore.current?.template_3d[0].attributes?.variables
            ) as TemplateVariable,
        [productDataStore]
    );

    const isCustomInnerDrawer = useMemo(() => {
        const drawerType =
            productDataStore?.current?.cabinet?.attributes?.drawer_type;

        return (
            CabinetDrawerType[drawerType as keyof typeof CabinetDrawerType] ===
            CabinetDrawerType.inner_custom
        );
    }, [productDataStore?.current]);

    const buttonLabel = useMemo(() => {
        let product = 'Doors';

        if (values.drawers) {
            product = 'Drawers';
            if (values.shelves) {
                product = 'Doors and Drawers';
            }
        }
        return `${prefix} ${product}`;
    }, [values, prefix]);

    const handleClick = useCallback(() => {
        showSurveyForm();
        const isOpen = isDoorOpenRef.current;
        const animations = isOpen
            ? closeAnimations.current
            : openAnimations.current;

        if (animations.length) {
            Promise.all(
                animations.map(({animation}) => animation(undefined, !isOpen))
            )
                .then(() => {
                    isDoorOpenRef.current = !isOpen;
                    setPrefix(isOpen ? 'Open' : 'Close');
                })
                .catch(() => {
                    // no-op
                });
        }
    }, [showSurveyForm]);

    const setAnimationHandlers = useCallback(
        (
            {open, close}: AnimationHandler,
            type = AnimationType.DOOR,
            alwaysSnapClosed = false
        ) => {
            if (previewLocation === 'DOORS' || alwaysSnapClosed) {
                [...close].forEach((animation) => {
                    pendingSnapOpens.current.push(animation(0));
                });
            }

            // do not open drawers if it is hidden
            if (
                type == AnimationType.DOOR ||
                (type == AnimationType.DRAWER && !hidden)
            ) {
                if (
                    isDoorOpenRef.current &&
                    (!isCustomInnerDrawer || previewLocation !== 'DRAWERS')
                ) {
                    open.forEach((animation) => {
                        pendingSnapOpens.current.push(animation(0));
                    });
                } else if (
                    isCustomInnerDrawer &&
                    previewLocation === 'DRAWERS'
                ) {
                    // per T1E-31 behaviour will have the door default opened and drawers slide back for custom inner drawers
                    isDoorOpenRef.current = false;
                    setPrefix('Open');
                    if (type == AnimationType.DOOR) {
                        open.forEach((animation) => {
                            pendingSnapOpens.current.push(animation(0));
                        });
                    }
                }
            }

            openAnimations.current.push(
                ...open.map((animation) => ({
                    animation,
                    type,
                }))
            );

            closeAnimations.current.push(
                ...close.map((animation) => ({
                    animation,
                    type,
                }))
            );
        },
        [hidden, isCustomInnerDrawer, previewLocation]
    );

    const attachCornerDrawerAnimation = useCallback(
        (
            mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh,
            meshPos: Dimension,
            index: number
        ) => {
            setAnimationHandlers(
                toggleCornerDrawerHandler({
                    mesh,
                    insert: meshPos,
                    index,
                }),
                AnimationType.DRAWER
            );
        },
        []
    );

    const animationHandler = useCallback(
        (
            mesh: THREE.Group<THREE.Object3DEventMap>,
            operationGroup: OperationGroup,
            meshPos: Dimension,
            counter: number
        ) => {
            const getMeshSize = (m: THREE.Object3D) => {
                const bbox = new THREE.Box3().setFromObject(m);
                const s = new THREE.Vector3();
                bbox.getSize(s);
                return s;
            };

            const {isDrawer, isInnerDrawer} = getPartAttributes(
                operationGroup,
                variables,
                values
            );

            const keepsShakerPlacement =
                mesh.name === 'ShakerDoors' &&
                !isSplitDoorOperationGroup(operationGroup);

            const handleReturnDoor = ({
                mesh,
                variables,
                values,
                isRight,
                isPairDoor,
            }: {
                mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh;
                variables: TemplateVariable;
                values: PreviewFormValues;
                isRight: boolean;
                isPairDoor: boolean;
            }) =>
                createReturnDoorAnimationHandler({
                    mesh,
                    variables,
                    values,
                    isRight,
                    isPairDoor,
                    carcaseThickness,
                    hingeFixup: 'hinge',
                    getMeshSize,
                });

            if (operationGroup.IsRightDoor) {
                const returnDoorHandler = handleReturnDoor({
                    mesh,
                    variables,
                    values,
                    isRight: true,
                    isPairDoor: !!operationGroup.IsPairDoor,
                });
                if (returnDoorHandler) {
                    setAnimationHandlers(returnDoorHandler);
                } else {
                    if (operationGroup.IsPairDoor && !keepsShakerPlacement) {
                        const size = getMeshSize(mesh);
                        if (!variables.is3DoorProduct) {
                            mesh.position.x = size.x;
                        } else {
                            mesh.position.x += size.x;
                        }
                        mesh.scale.x = -1;
                    }
                    setAnimationHandlers(
                        toggleDoorHandler({
                            mesh,
                            variables,
                            isRightDoor: true,
                            degrees: hidden || raw ? 115 : 87,
                            isThreeDoor: variables.is3DoorProduct,
                            animationDelay: operationGroup.AnimationDelay
                                ? Number(operationGroup.AnimationDelay)
                                : undefined,
                        })
                    );
                }
            }

            if (operationGroup.IsLeftDoor) {
                const returnDoorHandler = handleReturnDoor({
                    mesh,
                    variables,
                    values,
                    isRight: false,
                    isPairDoor: !!operationGroup.IsPairDoor,
                });
                if (returnDoorHandler) {
                    setAnimationHandlers(returnDoorHandler);
                } else {
                    if (!keepsShakerPlacement) {
                        const size = getMeshSize(mesh);
                        mesh.position.x = -size.x;
                        mesh.scale.x = -1;
                    }
                    setAnimationHandlers(
                        toggleDoorHandler({
                            mesh,
                            variables,
                            degrees: hidden || raw ? 115 : 87,
                            animationDelay: operationGroup.AnimationDelay
                                ? Number(operationGroup.AnimationDelay)
                                : undefined,
                        })
                    );
                }
            }

            if (operationGroup.IsTopHangDoor) {
                if (variables.isHighSwing) {
                    const size = getMeshSize(mesh);
                    const doorBottomMargin =
                        Number(values?.cabinet_door_bottom) || 0;
                    mesh.position.set(
                        2,
                        size.y + size.y * -1,
                        size.y + doorBottomMargin
                    );
                    mesh.scale.y = -1;
                    setAnimationHandlers(
                        highSwingAnimationHandler(mesh, 'y', previewLocation)
                    );
                } else if (variables.isHighLift) {
                    const size = getMeshSize(mesh);
                    // mesh.scale.y = -1;
                    const liftDistance = size.y / -30.5;
                    const endLiftDistance = size.y - liftDistance;
                    setAnimationHandlers(
                        highLiftAnimationHandler(
                            mesh,
                            endLiftDistance,
                            previewLocation
                        )
                    );
                } else {
                    const size = getMeshSize(mesh);
                    mesh.position.x = -size.x;
                    mesh.position.y = size.y;
                    mesh.scale.x = -1;
                    mesh.scale.y = -1;
                    setAnimationHandlers(
                        toggleDoorHandler({
                            mesh,
                            variables,
                            isTopHangDoor: true,
                        })
                    );
                }
            }

            if (operationGroup.IsLeftApplianceMainDoor) {
                const size = getMeshSize(mesh);
                mesh.position.x = -size.x;
                mesh.scale.x = -1;
                setAnimationHandlers(
                    toggleDoorHandler({
                        mesh,
                        variables,
                        degrees: 87,
                    })
                );
            }

            if (operationGroup.IsRightApplianceMainDoor) {
                const size = getMeshSize(mesh);
                mesh.position.x = size.x;
                mesh.scale.x = -1;
                setAnimationHandlers(
                    toggleDoorHandler({
                        mesh,
                        variables,
                        isRightDoor: true,
                        degrees: 87,
                    })
                );
            }

            if (
                operationGroup.IsCornerLeftDoor ||
                operationGroup.IsCornerRightDoor
            ) {
                const isRightDoor = Boolean(operationGroup.IsCornerRightDoor);
                if (isRightDoor) {
                    // Swing onto the right leg via Y/X, matching RoomPlanner's corner-door placement.
                    mesh.position.y =
                        values.cabinet_left_width - values.cabinet_right_depth;
                    mesh.position.x +=
                        values.cabinet_right_width - values.cabinet_left_depth;
                    mesh.scale.x = -1;
                }
                setAnimationHandlers(
                    toggleCornerHandler({
                        mesh,
                        isRightDoor,
                    })
                );
            }

            if (isDrawer) {
                const animation = toggleDrawerHandler(
                    mesh,
                    operationGroup,
                    variables,
                    values,
                    counter
                );
                if (isInnerDrawer && animation?.close?.length) {
                    void animation.close[0](0);
                }
                setAnimationHandlers(animation, AnimationType.DRAWER);
            }

            if (operationGroup.IsRightLShape) {
                if (operationGroup.IsBifoldRightDoor) {
                    mesh.scale.x = -1;
                    mesh.position.x = getPantryAwareRightLShapeBifoldDoorX(
                        mesh,
                        meshPos.x,
                        Boolean(variables?.isLShapedPantry),
                        values.cabinet_right_width,
                        Number(values.cabinet_door_right),
                        exteriorThickness,
                        carcaseThickness
                    );
                    setAnimationHandlers(
                        toggleMainBifoldDoorHandler({
                            mesh,
                            isRightLShape: true,
                        })
                    );
                }

                if (operationGroup.IsBifoldLeftDoor) {
                    const size = getMeshSize(mesh);
                    setAnimationHandlers(
                        toggleExtendedBifoldDoorHandler(
                            mesh,
                            meshPos,
                            variables,
                            values,
                            exteriorThickness,
                            carcaseThickness,
                            true,
                            Boolean(operationGroup.IsLeftBifoldExtension),
                            size.y,
                            0
                        )
                    );
                }
            } else {
                if (operationGroup.IsBifoldLeftDoor) {
                    const size = getMeshSize(mesh);
                    const door = exteriorThickness || carcaseThickness || 16.5;
                    setAnimationHandlers(
                        toggleMainBifoldDoorHandler({
                            mesh,
                            pivotOffset: size.x - door,
                        })
                    );
                }

                if (operationGroup.IsBifoldRightDoor) {
                    setAnimationHandlers(
                        toggleExtendedBifoldDoorHandler(
                            mesh,
                            meshPos,
                            variables,
                            values,
                            exteriorThickness,
                            carcaseThickness,
                            false,
                            false,
                            0,
                            0,
                            operationGroup.AppliedDoorShift?.insertDepth ?? 0,
                            operationGroup.AppliedDoorShift?.alongLeg ?? 0
                        )
                    );
                }
            }

            if (operationGroup.IsLeftApplianceExtendedDoor) {
                const size = getMeshSize(mesh);
                mesh.position.x = size.x;
                mesh.scale.x = -1;
                setAnimationHandlers(
                    toggleApplianceExtendedDoorHandler({
                        mesh,
                        variables,
                        values,
                        carcaseThickness,
                    })
                );
            }

            if (operationGroup.IsRightApplianceExtendedDoor) {
                mesh.scale.x = -1;
                setAnimationHandlers(
                    toggleApplianceExtendedDoorHandler({
                        mesh,
                        variables,
                        values,
                        carcaseThickness,
                        isRightExtendedDoor: true,
                    })
                );
            }

            if (operationGroup.IsCornerLeft || operationGroup.IsCornerRight) {
                setAnimationHandlers(
                    toggleCornerDrawerHandler({
                        mesh,
                        insert: meshPos,
                        index: Number(operationGroup.Index),
                    })
                );
            }

            if (operationGroup.IsCorFoldKitRightDoor) {
                setAnimationHandlers(
                    toggleFoldKitDoorHandler({
                        mesh,
                        isRightDoor: true,
                        values,
                        carcaseThickness,
                    }),
                    AnimationType.DOOR,
                    true
                );
            }

            if (operationGroup.IsCorFoldKitLeftDoor) {
                mesh.position.x = isShakerFoldKitDoor(mesh, operationGroup)
                    ? getFoldKitReturnPanelWidth(values)
                    : -getMeshSize(mesh).x;
                setAnimationHandlers(
                    toggleFoldKitDoorHandler({
                        mesh,
                        values,
                        carcaseThickness,
                    }),
                    AnimationType.DOOR,
                    true
                );
            }
        },
        [
            variables,
            values,
            carcaseThickness,
            exteriorThickness,
            hidden,
            raw,
            previewLocation,
        ]
    );

    const clearHandlers = useCallback(() => {
        openAnimations.current = [];
        closeAnimations.current = [];
        pendingSnapOpens.current = [];
    }, []);

    // Awaits queued snap-open tweens so the camera fit measures a settled scene.
    const waitForSnapOpens = useCallback(async () => {
        const pending = pendingSnapOpens.current;
        pendingSnapOpens.current = [];
        if (!pending.length) return;
        await Promise.race([
            Promise.allSettled(pending),
            new Promise((resolve) => setTimeout(resolve, SNAP_OPEN_TIMEOUT_MS)),
        ]);
    }, []);

    return {
        setButtonPrefix: setPrefix,
        buttonLabel,
        handleClick,
        animationHandler,
        isDoorOpenRef,
        clearHandlers,
        attachCornerDrawerAnimation,
        waitForSnapOpens,
    };
};

export default useDoorAndDrawerAnimation;
